/**
 * A4 工单收口页 — V1 手动导入
 *
 * Stream 2 Phase 2-3b / Plan §10 Stream 2 / Design §5.2 A4 工单收口.
 *
 * V1 范围：
 *  - V1 Banner（必显）：说明飞书/企微自动同步待 OPEN-18 关闭
 *  - 手动导入工单 Modal（ImportTicketModal）
 *  - 工单列表 + 分页 + 多维度筛选
 *  - 派单 Modal（AssignTicketModal）
 *  - 推进状态 Modal（UpdateStatusModal；resolved/closed 时 resolutionNote 必填）
 *  - 补充关联 Modal（LinkContextModal；前端校验至少一字段）
 *  - 工单详情 Drawer（TicketDetailDrawer）
 *  - relatedOrderId → 跳 /orders/:id（已可用）
 *  - relatedUserId / relatedWorkId / relatedChapterId → disabled + tooltip（等 Stream 2 Phase 2-4 / Stream 3）
 *
 * 权限：TICKET_READ 作为页面级守卫；派单 / 推进 / 关联 / 导入按钮挂 TICKET_HANDLE。
 */
import { useState } from 'react';
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Result,
  Card,
  Alert,
  Tooltip,
  Typography,
  Modal,
  Drawer,
  Descriptions,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listTickets,
  getTicket,
  importTicket,
  assignTicket,
  updateTicketStatus,
  linkTicketContext,
  type TicketListItem,
  type TicketDetail,
  type TicketSource,
  type TicketStatus,
  type TicketSyncStatus,
  type ListTicketsFilter,
} from '../../api/tickets';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

// ---------------------------------------------------------------------------
// Color / Label helpers
// ---------------------------------------------------------------------------

function sourceColor(source: TicketSource): string {
  switch (source) {
    case 'feishu':      return 'purple';
    case 'wechat_work': return 'green';
    case 'manual':      return 'default';
    default:            return 'default';
  }
}

function sourceLabel(source: TicketSource): string {
  switch (source) {
    case 'feishu':      return '飞书';
    case 'wechat_work': return '企微';
    case 'manual':      return '手动';
    default:            return source;
  }
}

function statusColor(status: TicketStatus): string {
  switch (status) {
    case 'open':        return 'blue';
    case 'in_progress': return 'gold';
    case 'resolved':    return 'green';
    case 'closed':      return 'default';
    default:            return 'default';
  }
}

function statusLabel(status: TicketStatus): string {
  switch (status) {
    case 'open':        return '待处理';
    case 'in_progress': return '处理中';
    case 'resolved':    return '已解决';
    case 'closed':      return '已关闭';
    default:            return status;
  }
}

function syncStatusColor(syncStatus: TicketSyncStatus): string {
  switch (syncStatus) {
    case 'synced':      return 'green';
    case 'pending':     return 'gold';
    case 'out_of_sync': return 'red';
    case 'manual':      return 'default';
    default:            return 'default';
  }
}

function syncStatusLabel(syncStatus: TicketSyncStatus): string {
  switch (syncStatus) {
    case 'synced':      return '已同步';
    case 'pending':     return '待同步';
    case 'out_of_sync': return '不同步';
    case 'manual':      return '手动';
    default:            return syncStatus;
  }
}

/** ISO 日期 → YYYY-MM-DD HH:mm */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ---------------------------------------------------------------------------
// Modal: 导入工单
// ---------------------------------------------------------------------------

interface ImportTicketModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function ImportTicketModal({ open, onClose, onSuccess }: ImportTicketModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return importTicket({
        source: v.source,
        externalTicketId: v.externalTicketId,
        sourceUrl: v.sourceUrl || undefined,
        title: v.title,
        description: v.description || undefined,
        reporterName: v.reporterName || undefined,
        reporterContact: v.reporterContact || undefined,
        relatedUserId: v.relatedUserId || undefined,
        relatedWorkId: v.relatedWorkId || undefined,
        relatedChapterId: v.relatedChapterId || undefined,
        relatedOrderId: v.relatedOrderId || undefined,
      });
    },
    onSuccess: () => {
      message.success('工单导入成功');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '导入工单失败');
    },
  });

  return (
    <Modal
      title="导入工单"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => {
          // validation failed — antd shows inline error, no further action needed
        });
      }}
      confirmLoading={mutation.isPending}
      okText="提交导入"
      data-testid="import-ticket-modal"
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="source"
          label="来源"
          rules={[{ required: true, message: '请选择工单来源' }]}
        >
          <Select
            placeholder="请选择来源"
            options={[
              { value: 'feishu', label: '飞书' },
              { value: 'wechat_work', label: '企微' },
              { value: 'manual', label: '手动' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="externalTicketId"
          label="外部工单 ID"
          rules={[{ required: true, message: '请输入外部工单 ID' }]}
        >
          <Input placeholder="externalTicketId" />
        </Form.Item>

        <Form.Item name="sourceUrl" label="来源链接（可选）">
          <Input placeholder="https://..." />
        </Form.Item>

        <Form.Item
          name="title"
          label="工单标题"
          rules={[{ required: true, message: '请输入工单标题' }]}
        >
          <Input placeholder="工单标题" />
        </Form.Item>

        <Form.Item name="description" label="描述（可选）">
          <TextArea rows={3} placeholder="工单详细描述" />
        </Form.Item>

        <Form.Item name="reporterName" label="反馈人姓名（可选）">
          <Input placeholder="reporterName" />
        </Form.Item>

        <Form.Item name="reporterContact" label="反馈人联系方式（可选）">
          <Input placeholder="reporterContact" />
        </Form.Item>

        <Form.Item name="relatedUserId" label="关联用户 ID（可选）">
          <Input placeholder="relatedUserId" />
        </Form.Item>

        <Form.Item name="relatedWorkId" label="关联作品 ID（可选）">
          <Input placeholder="relatedWorkId" />
        </Form.Item>

        <Form.Item name="relatedChapterId" label="关联章节 ID（可选）">
          <Input placeholder="relatedChapterId" />
        </Form.Item>

        <Form.Item name="relatedOrderId" label="关联订单 ID（可选）">
          <Input placeholder="relatedOrderId" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: 派单
// ---------------------------------------------------------------------------

interface AssignTicketModalProps {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function AssignTicketModal({ open, ticketId, onClose, onSuccess }: AssignTicketModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return assignTicket(ticketId!, { assigneeId: v.assigneeId });
    },
    onSuccess: () => {
      message.success('派单成功');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '派单失败');
    },
  });

  return (
    <Modal
      title="派单"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => {
          // validation failed — antd shows inline error, no further action needed
        });
      }}
      confirmLoading={mutation.isPending}
      okText="确认派单"
      data-testid="assign-ticket-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="assigneeId"
          label="处理人 Admin ID"
          rules={[{ required: true, message: '请输入处理人 ID' }]}
        >
          <Input placeholder="admin user id" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: 推进状态
// ---------------------------------------------------------------------------

interface UpdateStatusModalProps {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function UpdateStatusModal({ open, ticketId, onClose, onSuccess }: UpdateStatusModalProps) {
  const [form] = Form.useForm();
  const [currentStatus, setCurrentStatus] = useState<TicketStatus | null>(null);

  const needsResolutionNote =
    currentStatus === 'resolved' || currentStatus === 'closed';

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return updateTicketStatus(ticketId!, {
        status: v.status,
        resolutionNote: v.resolutionNote || undefined,
      });
    },
    onSuccess: () => {
      message.success('状态已更新');
      form.resetFields();
      setCurrentStatus(null);
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '状态更新失败');
    },
  });

  function handleCancel() {
    form.resetFields();
    setCurrentStatus(null);
    onClose();
  }

  return (
    <Modal
      title="推进状态"
      open={open}
      onCancel={handleCancel}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => {
          // validation failed — antd shows inline error, no further action needed
        });
      }}
      confirmLoading={mutation.isPending}
      okText="确认推进"
      data-testid="update-status-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="status"
          label="目标状态"
          rules={[{ required: true, message: '请选择目标状态' }]}
        >
          <Select
            placeholder="请选择状态"
            onChange={(v) => {
              setCurrentStatus(v as TicketStatus);
              // 若从 resolved/closed 切回其他状态，清除 resolutionNote 的必填态
              if (v !== 'resolved' && v !== 'closed') {
                // keep value, just remove required rule dynamically via state
              }
            }}
            options={[
              { value: 'open', label: '待处理' },
              { value: 'in_progress', label: '处理中' },
              { value: 'resolved', label: '已解决' },
              { value: 'closed', label: '已关闭' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="resolutionNote"
          label={
            needsResolutionNote
              ? '解决说明（必填）'
              : '解决说明（可选；resolved/closed 时必填）'
          }
          rules={[
            {
              required: needsResolutionNote,
              message: '状态为 resolved 或 closed 时必须填写解决说明',
            },
          ]}
        >
          <TextArea
            rows={3}
            placeholder={
              needsResolutionNote
                ? '请填写解决说明（必填）'
                : '解决说明（可选）'
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: 补充关联上下文
// ---------------------------------------------------------------------------

interface LinkContextModalProps {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function LinkContextModal({ open, ticketId, onClose, onSuccess }: LinkContextModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      const body: Record<string, string> = {};
      if (v.relatedUserId?.trim()) body.relatedUserId = v.relatedUserId.trim();
      if (v.relatedWorkId?.trim()) body.relatedWorkId = v.relatedWorkId.trim();
      if (v.relatedChapterId?.trim()) body.relatedChapterId = v.relatedChapterId.trim();
      if (v.relatedOrderId?.trim()) body.relatedOrderId = v.relatedOrderId.trim();
      return linkTicketContext(ticketId!, body);
    },
    onSuccess: () => {
      message.success('关联上下文已更新');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '补充关联失败');
    },
  });

  function handleOk() {
    const v = form.getFieldsValue();
    const hasAny =
      v.relatedUserId?.trim() ||
      v.relatedWorkId?.trim() ||
      v.relatedChapterId?.trim() ||
      v.relatedOrderId?.trim();
    if (!hasAny) {
      message.warning('至少填写一个关联字段');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      title="补充关联上下文"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleOk}
      confirmLoading={mutation.isPending}
      okText="确认补充"
      data-testid="link-context-modal"
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="至少填写一个关联字段"
        data-testid="link-context-hint"
      />
      <Form form={form} layout="vertical">
        <Form.Item name="relatedUserId" label="关联用户 ID">
          <Input placeholder="relatedUserId" />
        </Form.Item>
        <Form.Item name="relatedWorkId" label="关联作品 ID">
          <Input placeholder="relatedWorkId" />
        </Form.Item>
        <Form.Item name="relatedChapterId" label="关联章节 ID">
          <Input placeholder="relatedChapterId" />
        </Form.Item>
        <Form.Item name="relatedOrderId" label="关联订单 ID">
          <Input placeholder="relatedOrderId" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Drawer: 工单详情
// ---------------------------------------------------------------------------

interface TicketDetailDrawerProps {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
}

function TicketDetailDrawer({ open, ticketId, onClose }: TicketDetailDrawerProps) {
  const query = useQuery({
    queryKey: ['ticket-detail', ticketId],
    queryFn: () => getTicket(ticketId!),
    enabled: open && ticketId != null,
  });

  const d: TicketDetail | undefined = query.data;

  return (
    <Drawer
      title="工单详情"
      open={open}
      onClose={onClose}
      width={680}
      data-testid="ticket-detail-drawer"
    >
      {query.isLoading && <Text>加载中…</Text>}
      {d && (
        <>
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="工单 ID">{d.id}</Descriptions.Item>
            <Descriptions.Item label="外部工单 ID">{d.externalTicketId}</Descriptions.Item>
            <Descriptions.Item label="来源">
              <Tag color={sourceColor(d.source)}>{sourceLabel(d.source)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="来源链接">
              {d.sourceUrl ? (
                <a href={d.sourceUrl} target="_blank" rel="noreferrer">{d.sourceUrl}</a>
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="标题">{d.title}</Descriptions.Item>
            <Descriptions.Item label="描述">{d.description ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="反馈人">{d.reporterName ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="反馈人联系方式">{d.reporterContact ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusColor(d.status)}>{statusLabel(d.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="同步状态">
              <Tag color={syncStatusColor(d.syncStatus)}>{syncStatusLabel(d.syncStatus)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="处理人">{d.assigneeId ?? '—'}</Descriptions.Item>
          </Descriptions>

          {/* 关联上下文区块 */}
          <Card size="small" title="关联上下文" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="关联订单">
                {d.relatedOrderId ? (
                  <Link to={`/orders/${d.relatedOrderId}`} data-testid="detail-order-link">
                    {d.relatedOrderId}
                  </Link>
                ) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="关联用户">
                {d.relatedUserId ? (
                  <Tooltip title="等 Stream 2 Phase 2-4 / Stream 3 接入">
                    <Text type="secondary" style={{ cursor: 'not-allowed' }}>
                      {d.relatedUserId}
                    </Text>
                  </Tooltip>
                ) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="关联作品">
                {d.relatedWorkId ? (
                  <Tooltip title="等 Stream 2 Phase 2-4 / Stream 3 接入">
                    <Text type="secondary" style={{ cursor: 'not-allowed' }}>
                      {d.relatedWorkId}
                    </Text>
                  </Tooltip>
                ) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="关联章节">
                {d.relatedChapterId ? (
                  <Tooltip title="等 Stream 2 Phase 2-4 / Stream 3 接入">
                    <Text type="secondary" style={{ cursor: 'not-allowed' }}>
                      {d.relatedChapterId}
                    </Text>
                  </Tooltip>
                ) : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 操作历史区块 */}
          <Card size="small" title="操作历史">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="导入人">{d.importedBy}</Descriptions.Item>
              <Descriptions.Item label="导入时间">{formatDateTime(d.importedAt)}</Descriptions.Item>
              <Descriptions.Item label="处理人">{d.assigneeId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="最后同步时间">
                {d.lastSyncedAt ? formatDateTime(d.lastSyncedAt) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="解决人">{d.resolvedBy ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="解决时间">
                {d.resolvedAt ? formatDateTime(d.resolvedAt) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="解决说明">{d.resolutionNote ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDateTime(d.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(d.updatedAt)}</Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Filter form
// ---------------------------------------------------------------------------

interface FilterFormValues {
  status?: TicketStatus | '';
  source?: TicketSource | '';
  syncStatus?: TicketSyncStatus | '';
  assigneeId?: string;
  relatedUserId?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

// ---------------------------------------------------------------------------
// Main page (inner, after permission check)
// ---------------------------------------------------------------------------

function TicketsPageInner() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [filter, setFilter] = useState<ListTicketsFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal / Drawer state
  const [importOpen, setImportOpen] = useState(false);
  const [assignId, setAssignId] = useState<string | null>(null);
  const [updateStatusId, setUpdateStatusId] = useState<string | null>(null);
  const [linkContextId, setLinkContextId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['tickets', filter, page, pageSize],
    queryFn: () => listTickets({ ...filter, page, pageSize }),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListTicketsFilter = {};
    if (v.status) next.status = v.status as TicketStatus;
    if (v.source) next.source = v.source as TicketSource;
    if (v.syncStatus) next.syncStatus = v.syncStatus as TicketSyncStatus;
    if (v.assigneeId?.trim()) next.assigneeId = v.assigneeId.trim();
    if (v.relatedUserId?.trim()) next.relatedUserId = v.relatedUserId.trim();
    if (v.dateRange?.[0]) next.fromDate = v.dateRange[0].toISOString();
    if (v.dateRange?.[1]) next.toDate = v.dateRange[1].toISOString();
    setFilter(next);
    setPage(1);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilter({});
    setPage(1);
  }

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  }

  // Table columns
  const columns: ColumnsType<TicketListItem> = [
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      render: (s: TicketSource) => (
        <Tag color={sourceColor(s)}>{sourceLabel(s)}</Tag>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title: string, row: TicketListItem) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => setDetailId(row.id)}
          data-testid={`ticket-title-${row.id}`}
        >
          <Tooltip title={title}>
            <Text ellipsis style={{ maxWidth: 180 }}>
              {title}
            </Text>
          </Tooltip>
        </Button>
      ),
    },
    {
      title: '外部工单 ID',
      dataIndex: 'externalTicketId',
      key: 'externalTicketId',
      width: 140,
      render: (id: string) => (
        <Tooltip title={id}>
          <Text ellipsis style={{ maxWidth: 120, display: 'block' }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: TicketStatus) => (
        <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>
      ),
    },
    {
      title: '同步状态',
      dataIndex: 'syncStatus',
      key: 'syncStatus',
      width: 90,
      render: (s: TicketSyncStatus) => (
        <Tag color={syncStatusColor(s)}>{syncStatusLabel(s)}</Tag>
      ),
    },
    {
      title: '反馈人',
      dataIndex: 'reporterName',
      key: 'reporterName',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '关联用户',
      dataIndex: 'relatedUserId',
      key: 'relatedUserId',
      width: 120,
      render: (id: string | null) => {
        if (!id) return '—';
        // TODO: Stream 2 Phase 2-4 后启用跳转 /users/:id
        return (
          <Tooltip title="等 Stream 2 Phase 2-4 / Stream 3 接入">
            <Text
              type="secondary"
              ellipsis
              style={{ maxWidth: 100, display: 'block', cursor: 'not-allowed' }}
              data-testid={`related-user-disabled-${id}`}
            >
              {id}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: '关联订单',
      dataIndex: 'relatedOrderId',
      key: 'relatedOrderId',
      width: 120,
      render: (id: string | null) => {
        if (!id) return '—';
        return (
          <Link to={`/orders/${id}`} data-testid={`related-order-link-${id}`}>
            <Tooltip title={id}>
              <Text ellipsis style={{ maxWidth: 100 }}>
                {id}
              </Text>
            </Tooltip>
          </Link>
        );
      },
    },
    {
      title: '处理人',
      dataIndex: 'assigneeId',
      key: 'assigneeId',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, row: TicketListItem) => (
        <Space size="small" wrap>
          <PermissionGate point={PERMISSIONS.TICKET_HANDLE}>
            <Button
              size="small"
              onClick={() => setAssignId(row.id)}
              data-testid={`assign-btn-${row.id}`}
            >
              派单
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.TICKET_HANDLE}>
            <Button
              size="small"
              onClick={() => setUpdateStatusId(row.id)}
              data-testid={`update-status-btn-${row.id}`}
            >
              推进状态
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.TICKET_HANDLE}>
            <Button
              size="small"
              onClick={() => setLinkContextId(row.id)}
              data-testid={`link-context-btn-${row.id}`}
            >
              补充关联
            </Button>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <span>
          工单收口（A4）
          <Text type="secondary" style={{ fontSize: 13, marginLeft: 12, fontWeight: 400 }}>
            客服收件箱 - V1 手动导入；飞书/企微自动同步待 OPEN-18 关闭
          </Text>
        </span>
      }
      data-testid="tickets-page"
    >
      {/* V1 范围 Banner（必显） */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="V1 范围说明"
        description="V1 仅支持手动导入工单 + 上下文展示。飞书/企微自动同步、回写外部工单待后续 phase。"
        data-testid="v1-scope-banner"
      />

      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <PermissionGate point={PERMISSIONS.TICKET_HANDLE}>
          <Button
            type="primary"
            onClick={() => setImportOpen(true)}
            data-testid="import-ticket-btn"
          >
            导入工单
          </Button>
        </PermissionGate>

        <Form
          form={filterForm}
          layout="inline"
          style={{ flex: 1, flexWrap: 'wrap', gap: 8 }}
          data-testid="tickets-filter-form"
        >
          <Form.Item name="status" label="状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 110 }}
              options={[
                { value: 'open', label: '待处理' },
                { value: 'in_progress', label: '处理中' },
                { value: 'resolved', label: '已解决' },
                { value: 'closed', label: '已关闭' },
              ]}
            />
          </Form.Item>
          <Form.Item name="source" label="来源">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 100 }}
              options={[
                { value: 'feishu', label: '飞书' },
                { value: 'wechat_work', label: '企微' },
                { value: 'manual', label: '手动' },
              ]}
            />
          </Form.Item>
          <Form.Item name="syncStatus" label="同步状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 100 }}
              options={[
                { value: 'synced', label: '已同步' },
                { value: 'pending', label: '待同步' },
                { value: 'out_of_sync', label: '不同步' },
                { value: 'manual', label: '手动' },
              ]}
            />
          </Form.Item>
          <Form.Item name="assigneeId" label="处理人 ID">
            <Input placeholder="assigneeId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="relatedUserId" label="关联用户 ID">
            <Input placeholder="relatedUserId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={applyFilters}
                data-testid="tickets-search"
              >
                查询
              </Button>
              <Button onClick={resetFilters} data-testid="tickets-reset">
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* 工单列表表格 */}
      <Table<TicketListItem>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        scroll={{ x: 1600 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        data-testid="tickets-table"
      />

      {/* 导入工单 Modal */}
      <ImportTicketModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={invalidate}
      />

      {/* 派单 Modal */}
      <AssignTicketModal
        open={assignId != null}
        ticketId={assignId}
        onClose={() => setAssignId(null)}
        onSuccess={invalidate}
      />

      {/* 推进状态 Modal */}
      <UpdateStatusModal
        open={updateStatusId != null}
        ticketId={updateStatusId}
        onClose={() => setUpdateStatusId(null)}
        onSuccess={invalidate}
      />

      {/* 补充关联 Modal */}
      <LinkContextModal
        open={linkContextId != null}
        ticketId={linkContextId}
        onClose={() => setLinkContextId(null)}
        onSuccess={invalidate}
      />

      {/* 工单详情 Drawer */}
      <TicketDetailDrawer
        open={detailId != null}
        ticketId={detailId}
        onClose={() => setDetailId(null)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Export: page-level PermissionGate (TICKET_READ as gate)
// ---------------------------------------------------------------------------

export default function TicketsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.TICKET_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有访问工单管理的权限，请联系管理员申请 user:read 权限。"
        />
      }
    >
      <TicketsPageInner />
    </PermissionGate>
  );
}
